/**
 * InputField.jsx - Modern Form Input Components Library
 * 
 * A clean, well-structured library of reusable form input components
 * with consistent styling, validation, and performance optimizations.
 * 
 * @author System
 * @version 2.0.0
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Button, 
  Input, 
  Textarea, 
  Select, 
  SelectItem, 
  Switch, 
  Slider,
  Chip,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "@nextui-org/react";
import { FaChevronDown, FaEye, FaEyeSlash, FaUpload, FaTimes } from "react-icons/fa";
import { parseDate } from "@internationalized/date";
import { DatePicker } from "@nextui-org/react";
import { PulseLoader } from "../../../components/Loader";

// Import utility functions and components
import { ChangeDateDDToYYYY, DateFormatorDD, CreateNewEntryButton } from "../../../utils/helper";
import { uploadFile, getDataWithFilterUrl, getAlldata } from "../../../controllers/APIController";
import InfoToolTip from "../../../components/utils/InfoToolTip";
import FieldInputTooltip from "../../../components/utils/FieldInputTooltip";
import { FieldList } from "../../../utils/constant";

// Add consistent styling for all input fields using unified design system
const inputFieldStyles = `
  .modern-input-field {
    background: var(--color-neutral-50, #fafafa) !important;
    border: 1px solid var(--color-neutral-300, #d4d4d4) !important;
    border-radius: var(--radius-lg, 0.75rem) !important;
    transition: all 0.2s ease !important;
    box-shadow: var(--shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.05)) !important;
  }
  
  .modern-input-field:hover {
    border-color: var(--color-neutral-400, #a3a3a3) !important;
    box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1)) !important;
  }
  
  .modern-input-field:focus-within {
    border-color: var(--color-primary-500, #10b981) !important;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
  }
  
  .modern-input-field .nextui-input,
  .modern-input-field .nextui-textarea,
  .modern-input-field .nextui-select-trigger {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-size: var(--font-size-sm, 0.875rem) !important;
    padding: var(--spacing-md, 0.75rem) var(--spacing-lg, 1rem) !important;
  }
  
  .modern-input-field .nextui-input-label {
    font-size: var(--font-size-sm, 0.875rem) !important;
    font-weight: var(--font-weight-medium, 500) !important;
    color: var(--color-neutral-700, #404040) !important;
    margin-bottom: var(--spacing-sm, 0.5rem) !important;
  }
  
  .modern-input-field .nextui-select-trigger {
    min-height: 52px !important;
  }
  
  .modern-input-field .nextui-select-trigger .nextui-select-value {
    color: var(--color-neutral-500, #737373) !important;
    font-size: var(--font-size-sm, 0.875rem) !important;
  }
  
  .modern-input-field .nextui-select-trigger .nextui-select-arrow {
    color: var(--color-neutral-700, #404040) !important;
    font-size: var(--font-size-sm, 0.875rem) !important;
  }
  
  .modern-input-field.error {
    border-color: var(--color-error, #ef4444) !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
  }
  
  .modern-input-field.error:focus-within {
    border-color: var(--color-error, #ef4444) !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
  }
  
  .field-label label {
    font-size: var(--font-size-sm, 0.875rem) !important;
    font-weight: var(--font-weight-medium, 500) !important;
    color: var(--color-neutral-700, #404040) !important;
    margin-bottom: var(--spacing-sm, 0.5rem) !important;
    display: block !important;
    line-height: 1.4 !important;
  }
  
  .field-wrapper {
    margin-bottom: var(--spacing-lg, 1rem) !important;
  }
`;

/**
 * TextInput Component
 * Basic text input field with validation and tooltip support
 */
export const TextInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const handleChange = useCallback((event) => {
    onChangeHandler(data?.title, event.target.value, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Input
            type="text"
            value={value || ""}
            placeholder={data?.placeholder || `Enter ${data?.title?.toLowerCase()}`}
            onChange={handleChange}
            disabled={data?.isDisable}
            startContent={data?.startContent}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
          />
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * PasswordInput Component
 * Password input with show/hide toggle
 */
export const PasswordInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback((event) => {
    onChangeHandler(data?.title, event.target.value, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const toggleVisibility = useCallback(() => setIsVisible(!isVisible), [isVisible]);
  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Input
            type={isVisible ? "text" : "password"}
            value={value || ""}
            placeholder={data?.placeholder || `Enter ${data?.title?.toLowerCase()}`}
            onChange={handleChange}
            disabled={data?.isDisable}
            startContent={data?.startContent}
            endContent={
              <button
                className="focus:outline-none"
                type="button"
                onClick={toggleVisibility}
              >
                {isVisible ? (
                  <FaEyeSlash className="text-2xl text-default-400 pointer-events-none" />
                ) : (
                  <FaEye className="text-2xl text-default-400 pointer-events-none" />
                )}
              </button>
            }
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * IntegerInput Component
 * Numeric input for integers with validation
 */
export const IntegerInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const handleChange = useCallback((event) => {
    const inputValue = event.target.value;
    // Only allow numbers
    if (inputValue === '' || /^\d+$/.test(inputValue)) {
      onChangeHandler(data?.title, inputValue, data?.type);
    }
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Input
            type="number"
            value={value || ""}
            placeholder={data?.placeholder || `Enter ${data?.title?.toLowerCase()}`}
            onChange={handleChange}
            disabled={data?.isDisable}
            startContent={data?.startContent}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
            min={data?.min}
            max={data?.max}
          />
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * SliderInput Component
 * Range slider input with value display
 */
export const SliderInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const handleChange = useCallback((newValue) => {
    onChangeHandler(data?.title, newValue, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Slider
            size="sm"
            step={data?.step || 1}
            minValue={data?.min || 0}
            maxValue={data?.max || 100}
            value={value || data?.min || 0}
            onChange={handleChange}
            className="w-full"
            color={hasError ? "danger" : "primary"}
            showTooltip={true}
            showOutline={true}
            classNames={{
              track: "drop-shadow-sm border border-default",
              filler: "bg-gradient-to-r from-emerald-500 to-emerald-600",
              thumb: "border-2 border-emerald-500",
              label: "tracking-tight text-default-500",
              value: "text-foreground text-medium",
            }}
          />
          <div className="text-sm text-gray-500 mt-1">
            Current value: {value || data?.min || 0}
          </div>
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * BooleanInput Component
 * Toggle switch for boolean values
 */
export const BooleanInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const handleChange = useCallback((checked) => {
    onChangeHandler(data?.title, checked, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input flex items-center gap-3">
          <Switch
            isSelected={value || false}
            onValueChange={handleChange}
            disabled={data?.isDisable}
            color="success"
            size="sm"
          />
          <span className="text-sm text-gray-600">
            {value ? "Enabled" : "Disabled"}
          </span>
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * TextAreaInput Component
 * Multi-line text input with validation
 */
export const TextAreaInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const handleChange = useCallback((event) => {
    onChangeHandler(data?.title, event.target.value, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Textarea
            value={value || ""}
            placeholder={data?.placeholder || `Enter ${data?.title?.toLowerCase()}`}
            onChange={handleChange}
            disabled={data?.isDisable}
            minRows={data?.minRows || 3}
            maxRows={data?.maxRows || 8}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
          />
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * DateInput Component
 * Date picker input with validation
 */
export const DateInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const handleChange = useCallback((date) => {
    if (date) {
      const formattedDate = ChangeDateDDToYYYY(date);
      onChangeHandler(data?.title, formattedDate, data?.type);
    }
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];
  const currentDate = value ? parseDate(value) : null;

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <DatePicker
            value={currentDate}
            onChange={handleChange}
            isDisabled={data?.isDisable}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
            placeholder={data?.placeholder || "Select a date"}
          />
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * DropDownInput Component
 * Custom dropdown with search and multi-select capabilities
 */
export const DropDownInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelectionChange = useCallback((keys) => {
    const selectedValue = Array.from(keys)[0];
    onChangeHandler(data?.title, selectedValue, data?.type);
    setIsOpen(false);
  }, [data?.title, data?.type, onChangeHandler]);

  const filteredOptions = useMemo(() => {
    if (!data?.values || !Array.isArray(data.values)) return [];
    
    return data.values.filter(option => {
      if (typeof option === 'string') {
        return option.toLowerCase().includes(searchQuery.toLowerCase());
      }
      if (option && typeof option === 'object' && option.name) {
        return option.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return false;
    });
  }, [data?.values, searchQuery]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Select
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            selectedKeys={value ? [value] : []}
            onSelectionChange={handleSelectionChange}
            disabled={data?.isDisable}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
            placeholder={data?.placeholder || "Select an option"}
            startContent={
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 modern-input-field"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            }
          >
            {filteredOptions.map((option, index) => {
              const optionValue = typeof option === 'string' ? option : option.name || option.id || index;
              const optionLabel = typeof option === 'string' ? option : option.name || option.label || optionValue;
              
              return (
                <SelectItem key={optionValue} value={optionValue}>
                  {optionLabel}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        {/* Helper information below the input */}
        {data?.values && (
          <div className="field-helper">
            <div className="flex flex-col gap-1 text-xs text-gray-500">
              <span style={{color: 'red', fontWeight: 'bold', fontSize: '16px'}}>🔥 FIRE Options available: {filteredOptions.length}</span>
              {value && (
                <span style={{color: 'blue', fontWeight: 'bold', fontSize: '16px'}}>💧 WATER Selected: {value}</span>
              )}
            </div>
          </div>
        )}

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * RelationDropDownInput Component
 * Dropdown for related entities with create options
 */
export const RelationDropDownInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  onClickHandler, 
  buttonCSS = "mb-16", 
  cssStyle = "mb-12"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Process related data
  const relatedTo = data?.relatedto || data?.relatedTo || "";
  const metricIDList = relatedTo ? relatedTo.split(",") : [];

  const createEntryButtons = useMemo(() => {
    return metricIDList.map((metricId) => ({
      id: `create-${metricId}`,
      name: (
        <CreateNewEntryButton
          name={metricId}
          metricName={data?.metricname}
          onClickHandler={onClickHandler}
        />
      ),
    }));
  }, [metricIDList, data?.metricname, onClickHandler]);

  const allOptions = useMemo(() => {
    const baseOptions = data?.values || [];
    return [...createEntryButtons, ...baseOptions].filter(Boolean);
  }, [createEntryButtons, data?.values]);

  const filteredOptions = useMemo(() => {
    return allOptions.filter(item => {
      if (typeof item.name === 'string') {
        return item.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true; // Keep React components
    });
  }, [allOptions, searchQuery]);



  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Select
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            selectedKeys={value ? [value] : []}
            onSelectionChange={(keys) => {
              const selectedValue = Array.from(keys)[0];
              if (selectedValue) {
                onChangeHandler(data?.title, selectedValue, data?.type);
              }
            }}
            disabled={data?.isDisable}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
            placeholder={data?.placeholder || "Select an option"}
            startContent={
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            }
          >
            {filteredOptions.map((item, index) => {
              const itemKey = item.id || `item-${index}`;
              const optionValue = typeof item.name === 'string' ? item.name : item.id || index;
              const optionLabel = typeof item.name === 'string' ? item.name : item.name;
              
              return (
                <SelectItem key={itemKey} value={optionValue}>
                  {optionLabel}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        {/* Helper information below the input */}
        <div className="field-helper">
          <div className="flex flex-col gap-1 text-xs text-gray-500">
            <span style={{color: 'red', fontWeight: 'bold', fontSize: '16px'}}>🔥 FIRE Items available: {allOptions.length}</span>
            {value && (
              <span style={{color: 'blue', fontWeight: 'bold', fontSize: '16px'}}>💧 WATER Form value: {value}</span>
            )}
          </div>
        </div>



        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * FileInput Component
 * File upload input with validation and display
 */
export const FileInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-16", 
  inputfiledId
}) => {
  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      onChangeHandler(data?.title, file, data?.type);
    }
  }, [data?.title, data?.type, onChangeHandler]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        {hasError && (
          <p className="text-red-500 text-sm p-1">
            {validationErrors[data?.title]}
          </p>
        )}
        
        <div className="field-label">
          <label htmlFor={inputfiledId} className="text-sm font-medium text-gray-700">
            {value?.url || data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <div className={`flex flex-col items-center gap-2 text-sm border-2 border-dashed border-emerald-300 px-4 py-6 rounded-lg max-w-max cursor-pointer transition-all duration-200 hover:border-emerald-400 hover:bg-emerald-25 modern-input-field ${hasError ? 'error' : ''}`}>
            <FaUpload className="text-2xl text-emerald-500" />
            <span className="text-emerald-600 font-medium">Upload File</span>
            <span className="text-gray-500 text-xs">
              {data?.acceptfieldtype?.join(", ") || "All file types"}
            </span>
          </div>
        </div>
        
        <input
          type="file"
          id={inputfiledId}
          accept={data?.acceptfieldtype?.join(",") || "*"}
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * CustomFrequencyInput Component
 * Custom numeric input for frequency values
 */
export const CustomFrequencyInput = ({ 
  value, 
  validationErrors, 
  onChangeHandler 
}) => {
  const handleChange = useCallback((event) => {
    const inputValue = event.target.value;
    if (inputValue === '' || /^\d+$/.test(inputValue)) {
      onChangeHandler('frequency', inputValue, 'number');
    }
  }, [onChangeHandler]);

  const hasError = validationErrors && validationErrors['frequency'];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className="flex flex-col gap-2 mb-4 field-wrapper">
        <div className="field-label">
          <label>
            Frequency
            <span className="text-red-500 ml-1">*</span>
          </label>
        </div>
        
        <div className="field-input">
          <Input
            type="number"
            value={value || ""}
            placeholder="Enter frequency"
            onChange={handleChange}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors['frequency']}
            min={1}
          />
        </div>
      </div>
    </>
  );
};

/**
 * MultiSelectInput Component
 * Multi-select dropdown with chips display
 */
export const MultiSelectInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedValues = Array.isArray(value) ? value : [];
  
  const handleSelectionChange = useCallback((keys) => {
    const newValues = Array.from(keys);
    onChangeHandler(data?.title, newValues, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const handleRemoveItem = useCallback((itemToRemove) => {
    const newValues = selectedValues.filter(item => item !== itemToRemove);
    onChangeHandler(data?.title, newValues, data?.type);
  }, [selectedValues, data?.title, data?.type, onChangeHandler]);

  const filteredOptions = useMemo(() => {
    if (!data?.values || !Array.isArray(data.values)) return [];
    
    return data.values.filter(option => {
      const optionText = typeof option === 'string' ? option : option.name || option.label || '';
      return optionText.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [data?.values, searchQuery]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        {/* Selected items display */}
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedValues.map((item, index) => (
              <Chip
                key={`${item}-${index}`}
                variant="flat"
                color="success"
                onClose={() => handleRemoveItem(item)}
                className="text-sm"
              >
                {item}
              </Chip>
            ))}
          </div>
        )}
        
        <div className="field-input">
          <Select
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            selectedKeys={selectedValues}
            onSelectionChange={handleSelectionChange}
            selectionMode="multiple"
            disabled={data?.isDisable}
            className={`w-full modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && validationErrors[data?.title]}
            placeholder={data?.placeholder || "Select options"}
            startContent={
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 modern-input-field"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            }
          >
            {filteredOptions.map((option, index) => {
              const optionValue = typeof option === 'string' ? option : option.name || option.id || index;
              const optionLabel = typeof option === 'string' ? option : option.name || option.label || optionValue;
              
              return (
                <SelectItem key={optionValue} value={optionValue}>
                  {optionLabel}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * TreeInput Component
 * Hierarchical tree structure input
 */
export const TreeInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(value);

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const handleNodeSelect = useCallback((node) => {
    setSelectedNode(node);
    onChangeHandler(data?.title, node, data?.type);
  }, [data?.title, data?.type, onChangeHandler]);

  const renderTreeNode = useCallback((node, level = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode?.id === node.id;
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.id} className="ml-4">
        <div
          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
            isSelected 
              ? 'bg-emerald-100 border border-emerald-300' 
              : 'hover:bg-gray-50'
          }`}
          onClick={() => handleNodeSelect(node)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.id);
              }}
              className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700"
            >
              <FaChevronDown 
                className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
          {!hasChildren && <div className="w-4" />}
          
          <span className={`text-sm ${isSelected ? 'font-medium text-emerald-700' : 'text-gray-700'}`}>
            {node.name || node.label}
          </span>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="ml-4">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  }, [expandedNodes, selectedNode, handleNodeSelect, toggleNode]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <div className="border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto modern-input-field">
            {data?.treeData ? (
              data.treeData.map(node => renderTreeNode(node))
            ) : (
              <div className="text-gray-500 text-center py-4">
                No tree data available
              </div>
            )}
          </div>
          
          {selectedNode && (
            <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-sm">
              Selected: <span className="font-medium text-emerald-700">{selectedNode.name || selectedNode.label}</span>
            </div>
          )}
        </div>

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * MetricSelectorInput Component
 * Dynamic metric selector with modal
 */
export const MetricSelectorInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Fetch metrics on component mount
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const response = await getAlldata('metrics');
        if (response && response.data) {
          setMetrics(response.data);
        }
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const handleMetricSelect = useCallback((metric) => {
    onChangeHandler(data?.title, metric.id, data?.type);
    setIsModalOpen(false);
  }, [data?.title, data?.type, onChangeHandler]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(metrics.map(metric => metric.attributes?.Category).filter(Boolean))];
    return ['all', ...uniqueCategories];
  }, [metrics]);

  const filteredMetrics = useMemo(() => {
    let filtered = metrics;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(metric => metric.attributes?.Category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(metric => 
        metric.attributes?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        metric.attributes?.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [metrics, selectedCategory, searchQuery]);

  const selectedMetric = metrics.find(metric => metric.id.toString() === value?.toString());
  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title];

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Button 
            variant="bordered" 
            className={`w-full justify-between modern-input-field ${hasError ? 'error' : ''}`}
            onPress={() => setIsModalOpen(true)}
            size="sm"
          >
            <span>{selectedMetric?.attributes?.name || "Select a metric"}</span>
            <FaChevronDown />
          </Button>
        </div>

        {/* Metric selection modal */}
        {isModalOpen && createPortal(
          <Modal 
            isOpen={isModalOpen} 
            onOpenChange={setIsModalOpen}
            size="4xl"
            scrollBehavior="inside"
          >
            <ModalContent>
              <ModalHeader>
                <h3 className="text-lg font-semibold text-gray-900">
                  Select Metric
                </h3>
              </ModalHeader>
              <ModalBody>
                {/* Search and filter controls */}
                <div className="flex gap-4 mb-4">
                                     <Input
                     type="text"
                     placeholder="Search metrics..."
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                     className="flex-1 modern-input-field"
                     startContent={<FaChevronDown className="text-gray-400" />}
                   />
                  
                                     <Select
                     selectedKeys={[selectedCategory]}
                     onSelectionChange={(keys) => setSelectedCategory(Array.from(keys)[0])}
                     className="w-48 modern-input-field"
                     size="sm"
                   >
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                {/* Metrics list */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <PulseLoader variant="dots" color="emerald" size="large" />
                    </div>
                  ) : filteredMetrics.length > 0 ? (
                    filteredMetrics.map((metric) => (
                      <div
                        key={metric.id}
                        className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-emerald-300 hover:bg-emerald-25 transition-colors"
                        onClick={() => handleMetricSelect(metric)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {metric.attributes?.name}
                            </h4>
                            {metric.attributes?.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {metric.attributes.description}
                              </p>
                            )}
                            {metric.attributes?.Category && (
                              <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full mt-1">
                                {metric.attributes.Category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No metrics found matching your criteria
                    </div>
                  )}
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>,
          document.body
        )}

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * FormulaInput Component
 * Mathematical formula input with validation
 */
export const FormulaInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4"
}) => {
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const validateFormula = useCallback((formula) => {
    // Basic formula validation - can be enhanced based on requirements
    if (!formula) return { isValid: true, message: "" };
    
    // Check for basic mathematical operators and valid characters
    const validChars = /^[0-9+\-*/().\s]+$/;
    if (!validChars.test(formula)) {
      return { 
        isValid: false, 
        message: "Formula contains invalid characters. Only numbers, operators (+, -, *, /), and parentheses are allowed." 
      };
    }
    
    // Check for balanced parentheses
    let parenthesesCount = 0;
    for (let char of formula) {
      if (char === '(') parenthesesCount++;
      if (char === ')') parenthesesCount--;
      if (parenthesesCount < 0) {
        return { 
          isValid: false, 
          message: "Unbalanced parentheses detected." 
        };
      }
    }
    if (parenthesesCount !== 0) {
      return { 
        isValid: false, 
        message: "Unbalanced parentheses detected." 
      };
    }
    
    return { isValid: true, message: "" };
  }, []);

  const handleChange = useCallback((event) => {
    const formula = event.target.value;
    const validation = validateFormula(formula);
    
    setIsValid(validation.isValid);
    setErrorMessage(validation.message);
    
    if (validation.isValid) {
      onChangeHandler(data?.title, formula, data?.type);
    }
  }, [data?.title, data?.type, onChangeHandler, validateFormula]);

  const spacing = data?.title?.length > 100 ? "mb-6" : cssStyle;
  const hasError = validationErrors && validationErrors[data?.title] || !isValid;

  return (
    <>
      <style>{inputFieldStyles}</style>
      <div className={`flex flex-col gap-2 ${spacing} field-wrapper`}>
        <div className="field-label">
          <label>
            {data?.title}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div className="field-input">
          <Textarea
            value={value || ""}
            placeholder={data?.placeholder || "Enter mathematical formula (e.g., 2 + 3 * 4)"}
            onChange={handleChange}
            disabled={data?.isDisable}
            minRows={2}
            maxRows={4}
            className={`w-full font-mono modern-input-field ${hasError ? 'error' : ''}`}
            variant="bordered"
            color={hasError ? "danger" : "default"}
            description={hasError && (validationErrors?.[data?.title] || errorMessage)}
          />
        </div>

        {/* Formula preview */}
        {value && isValid && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="text-sm font-medium text-emerald-800 mb-1">Formula Preview:</div>
            <div className="font-mono text-emerald-700">{value}</div>
          </div>
        )}

        {/* Tooltip for additional information */}
        {moreInfo && (
          <div className="flex h-max">
            <InfoToolTip
              data={
                <FieldInputTooltip
                  data={moreInfo}
                  selectedFramework={data?.framework}
                  orgFrameworkDetails={orgFrameworkDetails}
                />
              }
            />
          </div>
        )}
      </div>
    </>
  );
};
